/**
 * @fileoverview User Management request error.
 * @description Shows a failed User Management request's message, with "Sign in again" when the
 * backend asks for a recent sign-in (403 / 2009: Update, Deactivate and Reactivate User, and Edit
 * User's choices, all need fresh MFA). Shared by the Edit, Deactivate and Reactivate dialogs.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { useLogout } from '@/shared/lib/auth/useLogout'

import type { ApiError } from '@/shared/types/http.types'

interface UserRequestErrorProps {
  error: ApiError
  /** The dialog's test id prefix; the message and button get "-error-message" and
   * "-sign-in-again-button" appended. */
  testIdPrefix: string
}

/**
 * @component
 * @param {UserRequestErrorProps} props - The failed request's error and the dialog's test id prefix.
 * @param {ApiError} props.error - The error to show.
 * @param {string} props.testIdPrefix - The dialog's test id prefix.
 */
function UserRequestError({ error, testIdPrefix }: UserRequestErrorProps) {
  const { t } = useTranslation()
  const logoutMutation = useLogout()

  return (
    <div
      role="alert"
      className="col-span-full text-[13px] text-danger"
      data-testid={`${testIdPrefix}-error-message`}
    >
      <p>{error.message}</p>
      {error.code === ERROR_CODES.STEP_UP_MFA_REQUIRED && (
        <button
          type="button"
          onClick={() => logoutMutation.mutate()}
          data-testid={`${testIdPrefix}-sign-in-again-button`}
          className="mt-1 cursor-pointer font-semibold text-primary underline"
        >
          {t('sales.userManagement.editUserForm.signInAgain')}
        </button>
      )}
    </div>
  )
}

export default UserRequestError
